import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { getData, postData } from "../../lib/utils/formHelpers";
import { useNavigate } from "react-router-dom";
import pathRoutes from "../routes/pathRoutes";
import { UserType } from "../types/genericTables.types";

export const setSession = (accessToken?: string) => {
  if (accessToken) {
    localStorage.setItem("token", accessToken);
  } else {
    localStorage.removeItem("token");
  }
};

type AuthContextType = {
  user?: UserType;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const initialState: AuthContextType = {
  user: undefined,
  isLoading: true,
  isInitialized: false,
  isAuthenticated: false,
  login: () => Promise.resolve() || Promise.reject(),
  logout: () => Promise.resolve(),
};

export const AuthContext = createContext<AuthContextType>(initialState);

type Props = {
  children: ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        const accessToken = window.localStorage.getItem("token");
        const userId = window.localStorage.getItem("user");
        if (accessToken && userId) {
          setSession(accessToken);
          const endpointAnalyst = "/users";

          const { data: responseAnalyst } = await getData(endpointAnalyst);

          const userRes = responseAnalyst.find(
            (analyst: any) => analyst.id === JSON.parse(userId).id
          );

          if (userRes) {
            setUser(userRes);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        setUser(null);
        setIsAuthenticated(false);
        navigate(pathRoutes.ROOT_LOGIN);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initialize();
  }, [navigate]);

  const login = async (username: string, password: string) => {
    const endpoint = "/login";
    setIsLoading(true);
    try {
      const { data: response } = await postData(endpoint, {
        username,
        password,
      });

      setSession(response.token);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(response.user));
      setUser(response.user);
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setSession();
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate(pathRoutes.ROOT_LOGIN);
  };

  const value = {
    user,
    isLoading,
    isInitialized,
    isAuthenticated,
    login,
    logout,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
const useAuth = () => useContext(AuthContext);

export default useAuth;
